import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { setTouchPrefs, useTouchPrefs } from '@/lib/touchPrefs';
import { setAwayMode, useAwayMode } from '@/lib/awayMode';
import { setStrikeZone, useStrikeZone } from '@/lib/strikeZonePref';
import { setBatterHelp, useBatterHelp } from '@/lib/batterHelpPref';
import { setBroadcast, useBroadcast } from '@/lib/broadcastPref';
import { setCatcherSigns, useCatcherSigns } from '@/lib/catcherSignsPref';
import { setGraphicsPref, useGraphicsPref, type GraphicsPref } from '@/lib/graphicsPref';
import { setThrowHand, useThrowHand } from '@/lib/throwHandPref';
import { getAudioSettings, setAudioSettings, subscribeAudio } from '@/lib/audio/sfx';
import { useSyncExternalStore } from 'react';
import { useCountryChoice, useFaceChoice } from '@/hooks/useCountryChoice';
import { CountryPicker } from '../Lobby/CountryPicker';
import { FacePicker } from '../Lobby/FacePicker';
import { countryColors } from '@shared/types';
import { adService } from '@/lib/ads/AdService';
import { DeleteAccount } from './DeleteAccount';

/** Botón ⚙️ con los ajustes de la app (país, rostro, controles y ayuda). */
export const SettingsMenu = ({ onLeave }: { onLeave?: () => void } = {}) => {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  // Desplazamiento horizontal para que el panel no se salga de la pantalla: se alinea a la derecha
  // del ⚙️, y en un celular de 360 px, con el avatar y «Salir» a su derecha, asomaba por la izquierda.
  const [shiftX, setShiftX] = useState(0);
  const shiftRef = useRef(0);
  useLayoutEffect(() => {
    if (!open) return;
    const fit = () => {
      const el = panel.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      // Posición sin el desplazamiento actual.
      const left = r.left - shiftRef.current;
      const right = r.right - shiftRef.current;
      const margin = 8;
      const next = left < margin ? margin - left : right > innerWidth - margin ? innerWidth - margin - right : 0;
      shiftRef.current = next;
      setShiftX(next);
    };
    fit();
    window.addEventListener('resize', fit);
    return () => {
      window.removeEventListener('resize', fit);
      shiftRef.current = 0;
      setShiftX(0);
    };
  }, [open]);
  const touch = useTouchPrefs();
  const away = useAwayMode();
  const zone = useStrikeZone();
  const batterHelp = useBatterHelp();
  const broadcast = useBroadcast();
  const signs = useCatcherSigns();
  const graphics = useGraphicsPref();
  const throwHand = useThrowHand();
  const audio = useSyncExternalStore(subscribeAudio, getAudioSettings, getAudioSettings);
  const [country, setCountry] = useCountryChoice();
  const faceChoice = useFaceChoice();
  const flagColors = countryColors(country);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      // La hoja del selector de país se abre fuera del menú (portal): tocarla no lo cierra.
      const t = e.target as Element | null;
      if (!box.current?.contains(t as Node) && !t?.closest?.('[data-keep-menu]')) setOpen(false);
    };
    window.addEventListener('pointerdown', close);
    return () => window.removeEventListener('pointerdown', close);
  }, [open]);

  return (
    <div ref={box} className="pointer-events-auto relative">
      <button
        type="button"
        aria-label="Ajustes"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="btn-ghost grid h-9 w-9 place-items-center text-base"
      >
        ⚙️
      </button>
      {open && (
        <div
          ref={panel}
          style={shiftX ? { transform: `translateX(${shiftX}px)` } : undefined}
          className="absolute right-0 top-11 z-50 max-h-[calc(100dvh-4rem)] w-64 space-y-3 overflow-y-auto overscroll-contain rounded-xl border border-white/10 bg-ink-800/95 p-3 shadow-xl backdrop-blur">
          <div className="text-xs font-semibold text-white">Ajustes</div>
          <div className="space-y-1.5">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">País que representas</div>
            <CountryPicker value={country} onChange={setCountry} compact />
          </div>
          <div className="space-y-1.5">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Tu rostro</div>
            <FacePicker
              value={faceChoice.face}
              seed={faceChoice.seed}
              onChange={faceChoice.set}
              color={flagColors ? flagColors[0] : faceChoice.color}
              color2={flagColors?.[1]}
              compact
            />
          </div>
          <div className="space-y-1.5">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Controles táctiles</div>
            <div role="radiogroup" aria-label="Tamaño de los botones" className="grid grid-cols-2 gap-1">
              {(['normal', 'grande'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={touch.size === v}
                  onClick={() => setTouchPrefs({ size: v })}
                  className={`min-h-9 rounded-lg text-xs font-semibold ${touch.size === v ? 'bg-accent-500 text-ink-900' : 'bg-white/5 text-slate-200'}`}
                >
                  Botones {v}
                </button>
              ))}
            </div>
            <div role="radiogroup" aria-label="Lado del botón de acción" className="grid grid-cols-2 gap-1">
              {(['right', 'left'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={touch.actionSide === v}
                  onClick={() => setTouchPrefs({ actionSide: v })}
                  className={`min-h-9 rounded-lg text-xs font-semibold ${touch.actionSide === v ? 'bg-accent-500 text-ink-900' : 'bg-white/5 text-slate-200'}`}
                >
                  Acción a la {v === 'right' ? 'derecha' : 'izquierda'}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Al salir de la pantalla</div>
            <div role="radiogroup" aria-label="Al salir de la pantalla" className="grid grid-cols-2 gap-1">
              {(
                [
                  ['pause', 'Pausar'],
                  ['cpu', 'CPU juega por mí'],
                ] as const
              ).map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={away === v}
                  onClick={() => setAwayMode(v)}
                  className={`min-h-9 rounded-lg text-xs font-semibold ${away === v ? 'bg-accent-500 text-ink-900' : 'bg-white/5 text-slate-200'}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-slate-500">Pausar solo aplica si eres el único humano; con más jugadores, la CPU juega por ti.</p>
          </div>
          <div className="space-y-1.5">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Calidad gráfica</div>
            <div role="radiogroup" aria-label="Calidad gráfica" className="grid grid-cols-4 gap-1">
              {(
                [
                  ['auto', 'Auto'],
                  ['alta', 'Alta'],
                  ['media', 'Media'],
                  ['baja', 'Baja'],
                ] as Array<[GraphicsPref, string]>
              ).map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={graphics === v}
                  onClick={() => setGraphicsPref(v)}
                  className={`min-h-9 rounded-lg text-[11px] font-semibold ${graphics === v ? 'bg-accent-500 text-ink-900' : 'bg-white/5 text-slate-200'}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-slate-500">Auto baja la calidad sola si el juego va a menos de 30 FPS.</p>
          </div>
          <div className="space-y-1.5">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Lanzo con (béisbol)</div>
            <div role="radiogroup" aria-label="Mano de lanzar" className="grid grid-cols-2 gap-1">
              {(
                [
                  ['R', 'Derecha'],
                  ['L', 'Zurda'],
                ] as const
              ).map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={throwHand === v}
                  onClick={() => setThrowHand(v)}
                  className={`min-h-9 rounded-lg text-xs font-semibold ${throwHand === v ? 'bg-accent-500 text-ink-900' : 'bg-white/5 text-slate-200'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Público</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label={audio.crowdMuted ? 'Activar el público' : 'Silenciar el público'}
                aria-pressed={audio.crowdMuted}
                onClick={() => setAudioSettings({ crowdMuted: !audio.crowdMuted })}
                className="grid h-8 w-8 place-items-center rounded-lg bg-white/5 text-sm"
              >
                {audio.crowdMuted ? '🔇' : '📣'}
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                aria-label="Volumen del público"
                value={audio.crowdVolume}
                disabled={audio.crowdMuted}
                onChange={(e) => setAudioSettings({ crowdVolume: Number(e.target.value) })}
                className="flex-1 accent-cyan-400 disabled:opacity-40"
              />
            </div>
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-200">
            <input type="checkbox" className="h-4 w-4 accent-cyan-400" checked={zone} onChange={(e) => setStrikeZone(e.target.checked)} />
            Mostrar la zona de strike (béisbol)
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-200">
            <input type="checkbox" className="h-4 w-4 accent-cyan-400" checked={batterHelp} onChange={(e) => setBatterHelp(e.target.checked)} />
            Ayuda al batear: ver a qué zona apunta el lanzador
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-200">
            <input type="checkbox" className="h-4 w-4 accent-cyan-400" checked={signs} onChange={(e) => setCatcherSigns(e.target.checked)} />
            Señas del receptor al lanzar (sugerencia de tipo y zona)
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-200">
            <input type="checkbox" className="h-4 w-4 accent-cyan-400" checked={broadcast} onChange={(e) => setBroadcast(e.target.checked)} />
            Pantallas de transmisión (alineaciones, bateador, lanzador)
          </label>
          <Link to="/como-se-juega" onClick={() => setOpen(false)} className="block text-xs font-semibold text-accent-400 hover:underline">
            📖 Cómo se juega
          </Link>
          <div className="flex gap-3 text-xs">
            <Link to="/privacidad" onClick={() => setOpen(false)} className="text-slate-400 hover:underline">
              Privacidad
            </Link>
            <Link to="/terminos" onClick={() => setOpen(false)} className="text-slate-400 hover:underline">
              Términos
            </Link>
          </div>
          {adService.enabled && adService.needsConsent && (
            <button type="button" onClick={() => adService.resetConsent()} className="block text-xs text-slate-400 hover:underline">
              🛡️ Privacidad de anuncios
            </button>
          )}
          <DeleteAccount onDone={() => setOpen(false)} />
          <div className="text-[10px] text-slate-500">Versión {__APP_BUILD__}</div>
          {onLeave && (
            <button type="button" onClick={onLeave} className="w-full rounded-lg bg-red-600/80 py-2 text-xs font-bold text-white">
              Abandonar partido
            </button>
          )}
        </div>
      )}
    </div>
  );
};
